import { AppAccent } from 'src/app/core/types/app-accent.type';
import { AppVisualContextService } from '../../services/app-visual-context.service';
import { HostBinding, inject, Component, EventEmitter, Input, OnInit, OnChanges, Output } from '@angular/core';
import { CardHeaderDropdownAction } from 'src/app/core/models/card-header-dropdown-action.model';
import { NullSafe } from 'src/app/core/types/null-safe.type';
import type { MiniCardStatus } from '../mini-card/mini-card.component';

@Component({
  selector: 'app-card',
  templateUrl: './card.component.html',
  styleUrls: ['./card.component.css'],
})
export class CardComponent implements OnInit, OnChanges {
  @Input() accent?: AppAccent;
  private readonly visualContext = inject(AppVisualContextService);
  @HostBinding('class') get visualClass(): string {
    return this.accent === undefined ? '' : this.visualContext.classForInputs(this.accent);
  }

  @Input({ required: true }) title: string;
  @Input() subtitle: string;
  @Input() status?: MiniCardStatus;
  @Input() headerButtons: CardHeaderButton[] = [];
  @Input() footerButtons?: CardFooterButtons | null;

  // Eventos personalizados para os botões
  @Output() primaryFooterButtonClick = new EventEmitter<void>();
  @Output() secondaryFooterButtonClick = new EventEmitter<void>();
  @Output() tertiaryFooterButtonClick = new EventEmitter<void>();

  protected expandedAccordions = new Set<number>();

  get statusClass(): string {
    return this.visualContext.classForState(this.status?.state);
  }

  constructor() {}

  ngOnInit(): void {
  }

  ngOnChanges(): void {
    this.validateHeaderButtons();
  }

  private validateHeaderButtons(): void {
    // Validação dos botões do cabeçalho
    const typesParamsLink = ['number', 'string', 'boolean'];
    this.headerButtons = this.headerButtons.filter(button => button); // Remove elementos null ou undefined
    this.headerButtons.forEach(button => {
      if (button.callBack && button.routerLink) {
        throw new Error('callBack e routerLink não podem ser utilizados simultaneamente!');
      }
      if (button.routerLink && button.params) {
        let paramKeys = Object.keys(button.params);
        paramKeys.forEach(key => {
          let paramType = typeof button.params[key];
          if (button.params[key] == null) return;
          if (!typesParamsLink.includes(paramType)) {
            throw new Error(`Tipo de parâmetro '${key}' não é suportado para um routerLink. Os tipos suportados são: ${typesParamsLink.join(', ')}.`);
          }
        });
      }
      // Validação para ícones
      if (button.type === 'icon' && !button.icon) {
        throw new Error('Botões do tipo icon devem conter um ícone.');
      }
      // Validação para botões
      if (button.type === 'button' && !button.label) {
        throw new Error('Botões do tipo button devem ter um label.');
      }
      // Validação para dropdown
      if (button.type === 'dropdown' && !button.disabled) {
        if (button.callBack || button.routerLink) {
          throw new Error('Botões do tipo dropdown não devem conter callBack ou routerLink.');
        }
        // if (!button.actions || button.actions.length === 0) {
        //   throw new Error('Botões do tipo dropdown devem ter pelo menos uma ação.');
        // }
        if (!button.label && !button.icon) {
          throw new Error('Botões do tipo dropdown devem ter um label ou um ícone.');
        }
        button.actions.forEach(action => {
          if (action.children?.length) return;
          if (action.callBack && action.routerLink) {
            throw new Error('Ações de dropdown não podem ter callBack e routerLink simultaneamente!');
          }
          if (action.routerLink && action.params) {
            let paramKeys = Object.keys(action.params);
            paramKeys.forEach(key => {
              let paramType = typeof action.params[key];
              if (action.params[key] == null) return;
              if (!typesParamsLink.includes(paramType)) {
                throw new Error(
                  `Tipo de parâmetro '${key}' não é suportado para um routerLink de ação de dropdown. Os tipos suportados são: ${typesParamsLink.join(', ')}.`
                );
              }
            });
          }
        });
      }
    });
  }

  runCallBack(callBack: NullSafe<(...args: any[]) => void>, params?: { [name: string]: any }) {
    if (typeof callBack !== 'function') return;
    params ? callBack(...Object.values(params)) : callBack();
  }

  /** Expande/recolhe um accordion dentro do dropdown. stopPropagation evita que o dropdown feche. */
  toggleAccordion(index: number, event: Event): void {
    event.stopPropagation();
    this.expandedAccordions.has(index) ? this.expandedAccordions.delete(index) : this.expandedAccordions.add(index);
  }

  protected trackByIndex(index: number): number {
    return index;
  }
}

interface CardHeaderButtonInterface {
  type: 'icon' | 'button' | 'button-outline' | 'dropdown'; // Tipo do botão
  icon?: string;
  tooltip?: string;
  label?: string;
  labelShort?: string; // Label curto para telas menores (col, col-md). Se não informado, usa o label
  routerLink?: string;
  callBack?: (...args: any[]) => void;
  params?: { [name: string]: any };
  actions?: CardHeaderDropdownAction[]; // Ações para dropdown
  disabled?: boolean;
}

export type CardHeaderButton = NullSafe<CardHeaderButtonInterface>;

interface CardFooterButtonsInterface {
  primary?: { title: string; callBack?: (...args: any[]) => void; params?: { [name: string]: any }; disabled?: boolean } | null;
  secondary?: { title: string; callBack?: (...args: any[]) => void; params?: { [name: string]: any }; disabled?: boolean } | null;
  tertiary?: { title: string; callBack?: (...args: any[]) => void; params?: { [name: string]: any }; disabled?: boolean } | null;
}

export type CardFooterButtons = NullSafe<CardFooterButtonsInterface>;
